import { useState, useRef, useEffect, useCallback } from 'react';
import type { Message } from '../types';
import { useSpeechToText } from '../hooks/useSpeechToText';
import { ChatMessage } from './ChatMessage';
import { sanitizeForLogging, validateUserInput, validateAIResponse } from '../utils/sanitize';
import { SECURITY_CONFIG } from '../utils/security';

const USE_DEMO_MODE = import.meta.env.VITE_USE_DEMO_MODE === 'true';
const API_TOKEN = import.meta.env.VITE_API_TOKEN;

export const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);

  // Communicate size changes to parent window for iframe
  const updateIframeSize = (width: number, height: number) => {
    if (window.parent !== window) {
      window.parent.postMessage({ type: 'resize', width, height }, '*');
    }
  };

  // Listen for messages from parent window
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'openChatbot') {
        setIsOpen(true);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Handle open/close state changes
  useEffect(() => {
    const updateSize = () => {
      if (isOpen) {
        const width = Math.min(453, window.innerWidth - 32);
        const height = Math.min(704, window.innerHeight - 32);
        updateIframeSize(width, height);
      } else {
        updateIframeSize(80, 80);
      }
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, [isOpen]);
  const [selectedVoice, setSelectedVoice] = useState('beena');
  const [activeTab, setActiveTab] = useState<'text' | 'voice'>('voice');
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const currentAudioRef = useRef<AudioBufferSourceNode | null>(null);


  const voices = {
    beena: 'E8a7vP9b4GFO0M0HBEWq',
    jim: 'xsMs8cBLQVv1gqScpJqk', 
    nitin: 'kNSLTJx5xpNeXzHsw5DN',
    costi: 'hD7Tz59UdMiv2awIgwPC'
  };

  const voiceMessages: Record<string, string> = {
    beena: "My team leads the State of AI research we do every year. What would you like to learn more about?",
    jim: "My team leads the State of AI research we do every year. What would you like to learn more about?",
    nitin: "My team leads the State of AI research we do every year. What would you like to learn more about?",
    costi: "My team leads the State of AI research we do every year. What would you like to learn more about?"
  };

  // Custom TTS function using backend
  const textToSpeech = async (text: string, voiceId?: string): Promise<ArrayBuffer> => {
    try {
      const apiUrl = import.meta.env.VITE_API_BASE_URL || '';
      const useVoiceId = voiceId || voices[selectedVoice as keyof typeof voices];
      console.log('TTS Request:', { 
        text_length: text.length, 
        voice_id: useVoiceId, 
        selectedVoice,
        sanitized_preview: sanitizeForLogging(text).substring(0, 50) + (text.length > 50 ? '...' : '')
      });
      
      const response = await fetch(`${apiUrl}/api/tts/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${API_TOKEN}`
        },
        body: JSON.stringify({
          text,
          voice_id: useVoiceId
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('TTS API Error:', response.status, response.statusText, errorText);
        throw new Error(`TTS API error: ${response.status} ${response.statusText} - ${errorText}`);
      }

      return await response.arrayBuffer();
    } catch (error) {
      console.error('Error in textToSpeech:', error);
      throw error;
    }
  };

  const scrollToBottom = () => {
    // Only scroll if not in an iframe to prevent parent page scrolling
    if (window.parent === window) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const playAudio = async (audioBuffer: ArrayBuffer) => {
    try {
      // Stop any currently playing audio
      if (currentAudioRef.current) {
        currentAudioRef.current.stop();
        currentAudioRef.current = null;
      }

      const audioContext = new AudioContext();
      const audioBufferDecoded = await audioContext.decodeAudioData(audioBuffer);
      const source = audioContext.createBufferSource();
      source.buffer = audioBufferDecoded;
      source.connect(audioContext.destination);
      
      // Store reference to current audio source
      currentAudioRef.current = source;
      
      // Clear reference when audio ends
      source.onended = () => {
        currentAudioRef.current = null;
      };
      
      source.start();
    } catch (error) {
      console.error('Error playing audio:', error);
      throw new Error('Failed to play audio');
    }
  };

  const processTextMessage = useCallback(async (text: string) => {
    // Validate input before processing
    const validation = validateUserInput(text);
    if (!validation.isValid) {
      console.warn('Invalid input blocked:', validation.reason);
      const errorMessage: Message = {
        id: Date.now().toString(),
        text: 'Your message contains invalid content. Please try rephrasing your question.',
        isUser: false,
        timestamp: new Date(),
        speakerName: selectedVoice.charAt(0).toUpperCase() + selectedVoice.slice(1)
      };
      setMessages(prev => [...prev, errorMessage]);
      return;
    }

    const sanitizedText = validation.sanitized;
    if (!sanitizedText.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: sanitizedText,
      isUser: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsProcessing(true);

    try {
      let botResponse: string = '';
      let audioBufferData: ArrayBuffer | null = null;
      // Capture voice ID at the time of processing to avoid race conditions
      const currentVoiceId = voices[selectedVoice as keyof typeof voices];

      if (USE_DEMO_MODE) {
        // Demo mode - use hardcoded response with validation (no user input reflection)
        const demoResponse = `I got your message. This is a demo response from ElevenLabs voice.`;
        botResponse = validateAIResponse(demoResponse);
        
        // Create bot message for demo mode
        const botMessage: Message = {
          id: (Date.now() + 1).toString(),
          text: botResponse,
          isUser: false,
          timestamp: new Date(),
          speakerName: selectedVoice.charAt(0).toUpperCase() + selectedVoice.slice(1)
        };
        setMessages(prev => [...prev, botMessage]);
      } else {
        // API mode - first get AI response, then convert to speech
        const apiUrl = import.meta.env.VITE_API_BASE_URL || '';
        
        // Get AI response with structured prompt to prevent injection
        const chatResponse = await fetch(`${apiUrl}/api/generate/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${API_TOKEN}`
          },
          body: JSON.stringify({ 
            text: sanitizedText,
            persona: selectedVoice.charAt(0).toUpperCase() + selectedVoice.slice(1),
            conversation: [{"role": "user", "content": sanitizedText}],
            // Add system-level protection
            systemPrompt: SECURITY_CONFIG.SYSTEM_PROMPT_PROTECTION.enabled 
              ? SECURITY_CONFIG.SYSTEM_PROMPT_PROTECTION.message 
              : undefined
          })
        });

        if (!chatResponse.ok) {
          throw new Error(`Chat API error: ${chatResponse.status} ${chatResponse.statusText}`);
        }

        // Create bot message immediately for progressive updates
        const botMessageId = (Date.now() + 1).toString();
        const initialBotMessage: Message = {
          id: botMessageId,
          text: '',
          isUser: false,
          timestamp: new Date(),
          speakerName: selectedVoice.charAt(0).toUpperCase() + selectedVoice.slice(1)
        };
        setMessages(prev => [...prev, initialBotMessage]);

        // Handle streaming response with progressive updates
        const reader = chatResponse.body?.getReader();
        const decoder = new TextDecoder();
        botResponse = '';
        
        if (reader) {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            
            const chunk = decoder.decode(value);
            const lines = chunk.split('\n');
            
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const data = line.slice(6);
                if (data.trim() && data !== '[DONE]') {
                  try {
                    const parsed = JSON.parse(data);
                    const newContent = parsed.message || parsed.content || parsed.text || '';
                    if (newContent) {
                      botResponse += newContent;
                      // Update message progressively
                      setMessages(prev => prev.map(msg => 
                        msg.id === botMessageId 
                          ? { ...msg, text: botResponse }
                          : msg
                      ));
                    }
                  } catch (e) {
                    // Skip invalid JSON chunks
                  }
                }
              }
            }
          }
        }
  
        if (!botResponse) {
          botResponse = 'I received your message.';
        }
        
        // Final validation of complete response
        botResponse = validateAIResponse(botResponse);
        
        // Convert AI response to speech
        const ttsResponse = await fetch(`${apiUrl}/api/tts/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${API_TOKEN}`
          },
          body: JSON.stringify({ text: botResponse, voice_id: currentVoiceId })
        });

        if (ttsResponse.ok) {
          audioBufferData = await ttsResponse.arrayBuffer();
        }
      }
      
      // Message already created and updated progressively above
      // No need to create/add again

      // Generate speech for the response only in voice mode
      if (activeTab === 'voice') {
        if (USE_DEMO_MODE) {
          const audioBuffer = await textToSpeech(botResponse, currentVoiceId);
          await playAudio(audioBuffer);
        } else if (audioBufferData) {
          // Use the audio buffer from the API response
          await playAudio(audioBufferData);
        }
      }
    } catch (error) {
      console.error('Error processing message:', error);
      
      // Show error message to user
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: 'Sorry, I encountered an error. Please try again.',
        isUser: false,
        timestamp: new Date(),
        speakerName: selectedVoice.charAt(0).toUpperCase() + selectedVoice.slice(1)
      };
      setMessages(prev => [...prev, errorMessage]);
    }

    setIsProcessing(false);
  }, [textToSpeech, selectedVoice, activeTab]);

  const {
    isListening,
    transcript,
    startListening,
    stopListening
  } = useSpeechToText();

  const processedTranscriptRef = useRef('');

  useEffect(() => {
    if (transcript && !isListening && transcript !== processedTranscriptRef.current) {
      console.log('Processing transcript:', {
        length: transcript.length,
        sanitized_preview: sanitizeForLogging(transcript).substring(0, 50) + (transcript.length > 50 ? '...' : '')
      });
      processedTranscriptRef.current = transcript;
      processTextMessage(transcript);
    }
  }, [transcript, isListening, processTextMessage]);

  const handleTextSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    processTextMessage(inputText);
    setInputText('');
  };

  if (!isOpen) {
    return (
      <div className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center fixed bottom-4 right-4 z-50">
        <button
          onClick={() => setIsOpen(true)}
          className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-[#43B02A] to-[#86BC25] text-white rounded-full transition-colors flex items-center justify-center"
          style={{ boxShadow: '0 0 10px 0 rgba(134, 188, 37, 0.60)' }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 53 53" fill="none">
            <path d="M10.8891 23.0842H13.9376V18.7291H10.8891V23.0842ZM17.4224 28.3109H20.4709V13.5024H17.4224V28.3109ZM24.6091 33.5376H27.6576V8.27577H24.6091V33.5376ZM31.7958 28.3109H34.8442V13.5024H31.7958V28.3109ZM38.3291 23.0842H41.3776V18.7291H38.3291V23.0842ZM0 52.2667V4.35577C0 3.17977 0.431853 2.1597 1.29556 1.29556C2.1597 0.431854 3.17977 0 4.35577 0H47.9109C49.0869 0 50.107 0.431854 50.9711 1.29556C51.8348 2.1597 52.2667 3.17977 52.2667 4.35577V37.4576C52.2667 38.6336 51.8348 39.6536 50.9711 40.5178C50.107 41.3815 49.0869 41.8133 47.9109 41.8133H10.4533L0 52.2667ZM8.58023 37.4576H47.9109V4.35577H4.35577V41.9224L8.58023 37.4576Z" fill="black"/>
          </svg>
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[704px] w-[453px] max-w-[calc(100vw-32px)] max-h-[calc(100vh-32px)] bg-white relative rounded-lg shadow-2xl fixed bottom-4 right-4 z-50">
      <button
        onClick={() => setIsOpen(false)}
        className="absolute top-3 right-4 sm:top-5 sm:right-10 text-gray-400 hover:text-gray-600 text-xl z-50"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
          <path d="M1.47458 21.8975L0 20.4229L9.47458 10.9488L0 1.47458L1.47458 0L10.9488 9.47458L20.4229 0L21.8975 1.47458L12.4229 10.9488L21.8975 20.4229L20.4229 21.8975L10.9488 12.4229L1.47458 21.8975Z" fill="currentColor"/>
        </svg>
      </button>
      
      <div className="p-3 sm:p-4 flex flex-col-reverse justify-between">

        <div className="flex gap-2 items-center">
          <img src={`/images/${selectedVoice}-small.png`} alt="" style={{ visibility: activeTab === 'text' || messages.length > 0 ? 'visible' : 'hidden' }} />
 <div className="flex flex-col-reverse">
        <div className="relative">
          <div 
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="rounded-full bg-gradient-to-r from-[#43B02A] to-[#86BC25] w-32 h-6 text-black text-lg font-bold leading-6 px-3 cursor-pointer flex items-center justify-between"
            style={{ fontFamily: '"Open Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}
          >
            <span>{selectedVoice.charAt(0).toUpperCase() + selectedVoice.slice(1)}</span>
            <svg className="relative left-[6px]" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
              <circle cx="9" cy="9" r="8.5" stroke="black"/>
              <path d="M5 8L9.19604 12L13 8" stroke="black"/>
            </svg>
          </div>
          {isDropdownOpen && (
            <div className="absolute top-7 left-0 w-32 bg-white rounded-lg shadow-lg z-50" style={{ fontFamily: '"Open Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
              {['beena', 'jim', 'nitin', 'costi'].map(voice => (
                <div
                  key={voice}
                  onClick={() => {
                    try {
                      setSelectedVoice(voice);
                      setIsDropdownOpen(false);
                    } catch (error) {
                      console.error('Error selecting voice:', error);
                    }
                  }}
                  className="px-3 py-2 text-black hover:bg-green-600 cursor-pointer first:rounded-t-lg last:rounded-b-lg"
                >
                  {voice.charAt(0).toUpperCase() + voice.slice(1)}
                </div>
              ))}
            </div>
          )}
        </div>
        <p className="text-black text-lg not-italic font-normal leading-6 mb-[7px]">You're chatting with AI author</p>
        </div>
       
        </div>

 
      </div>



    <div 
  className="flex-1 flex flex-col bg-cover bg-center bg-no-repeat w-full min-h-0 relative"
  style={{ 
    backgroundImage: 'url(/images/background.png)', 
    borderRadius: '50px 30px 50px 50px',
    background: 'linear-gradient(180deg, #0f4d64 52.54%, #114e63 99.93%)'
  }}
>
        <video 
          autoPlay 
          loop 
          muted 
          className={`absolute inset-0 w-full h-full -top-[-20px] rounded-[20%] ${messages.length > 0 || activeTab === 'text' ? 'hidden' : ''}`}
        >
          <source src="/video/background.mp4" type="video/mp4" />
        </video>
        <div className="flex items-center justify-between relative bottom-[16px] left-[16px] pt-4 pr-4 pl-4 z-10">
          <p className="text-white text-2xl not-italic font-bold leading-6">{activeTab === 'voice' ? 'Tap to speak' : "Let's chat"}</p>
          <div className="p-6 w-[180px] h-[100px] bg-[url('/images/rounded-border-bg.jpg')] bg-cover bg-no-repeat bg-[position:calc(50%)_calc(50%)]">
            <div className="flex gap-1 justify-between bg-[#F0F0F0] p-0.25 rounded-[20px] w-fit relative left-[36px] bottom-[16px]">
              <button
                onClick={() => setActiveTab('voice')}
                className={`p-2 transition-colors ${
                  activeTab === 'voice'
                    ? 'rounded-full border border-[#F0F0F0] bg-gradient-to-r from-[#43B02A] to-[#86BC25] text-white'
                    : 'bg-transparent text-gray-700 rounded'
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M5.44433 19.2V4.8H7.66667V19.2H5.44433ZM10.889 24V0H13.111V24H10.889ZM0 14.4V9.6H2.22233V14.4H0ZM16.3333 19.2V4.8H18.5557V19.2H16.3333ZM21.7777 14.4V9.6H24V14.4H21.7777Z" fill="black"/>
                </svg>
              </button>
              <button
                onClick={() => {
                  // Stop any currently playing audio when switching to text mode
                  if (currentAudioRef.current) {
                    currentAudioRef.current.stop();
                    currentAudioRef.current = null;
                  }
                  setActiveTab('text');
                }}
                className={`p-2 transition-colors ${
                  activeTab === 'text'
                    ? 'rounded-full border border-[#F0F0F0] bg-gradient-to-r from-[#43B02A] to-[#86BC25] text-white'
                    : 'bg-transparent text-gray-700 rounded'
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M0 24V2.0001C0 1.4601 0.1983 0.991701 0.5949 0.594901C0.9917 0.198301 1.4601 0 2.0001 0H21.9999C22.5399 0 23.0083 0.198301 23.4051 0.594901C23.8017 0.991701 24 1.4601 24 2.0001V17.1999C24 17.7399 23.8017 18.2083 23.4051 18.6051C23.0083 19.0017 22.5399 19.2 21.9999 19.2H4.8L0 24ZM3.9399 17.1999H21.9999V2.0001H2.0001V19.2501L3.9399 17.1999Z" fill="black"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto pr-4 pl-4 space-y-2 custom-scrollbar relative z-10" style={{ minHeight: 0, scrollbarWidth: 'thin', scrollbarColor: 'white transparent' }}>
        {messages.map(message => (
          <ChatMessage key={message.id} message={message} />
        ))}
          <div ref={messagesEndRef} />
        </div>

        <div className="pt-0 relative z-10" style={{ paddingBottom: '48.4px' }}>
        {activeTab === 'text' && (
          <form onSubmit={handleTextSubmit} className="flex justify-center gap-2 px-2" style={{ paddingTop: '20px' }}>
            <input
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="Type your prompt or question..."
              className="px-3 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 flex-1 max-w-[325px]"
              disabled={isProcessing}
            />
            <button
              type="submit"
              disabled={!inputText.trim() || isProcessing}
              className="px-2 rounded-[50%] disabled:transparent text-white rounded-lg transition-colors"
            >
              {/* <Send size={20} /> */}
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none">
  <circle cx="24" cy="24" r="24" fill="url(#paint0_linear_1691_1637)"/>
  <path d="M12 37.6667V11L43.6667 24.3333L12 37.6667ZM14.7779 33.4167L36.4721 24.3333L14.7779 15.1667V21.7779L24.8333 24.3333L14.7779 26.8333V33.4167Z" fill="black"/>
  <defs>
    <linearGradient id="paint0_linear_1691_1637" x1="0" y1="24" x2="48" y2="24" gradientUnits="userSpaceOnUse">
      <stop stop-color="#43B02A"/>
      <stop offset="1" stop-color="#86BC25"/>
    </linearGradient>
  </defs>
</svg>
            </button>
          </form>
        )}

        {activeTab === 'voice' && (
          <div>
            {messages.length === 0 && (
              <div className="text-center text-white flex justify-center min-h-[200px] pr-4 pl-4">
                <div className="flex flex-col mb-6">
                  <img className="w-[165px] h-[171px] mx-auto relative bottom-[32px]" src={`/images/${selectedVoice}.png`} alt={selectedVoice.charAt(0).toUpperCase() + selectedVoice.slice(1)} />
                  <p className="text-[rgba(255,255,255,0.50)] text-lg not-italic font-normal leading-6 w-[256px]"><span className="text-white text-center text-lg not-italic font-normal leading-6">Hi, I'm {selectedVoice.charAt(0).toUpperCase() + selectedVoice.slice(1)}.</span> {voiceMessages[selectedVoice]}</p>
                </div>
              </div>
            )}
            {/* {isListening && (
              <div className="text-blue-600 text-sm text-center mb-2">
                Listening... (speak now)
              </div>
            )} */}
            <div className="flex justify-center">
              <button
                onClick={isListening ? stopListening : startListening}
                className={`p-3 rounded-full transition-colors ${
                  isListening
                    ? 'text-white animate-pulse'
                    : 'text-white'
                }`}
                disabled={isProcessing}
              >
                {isListening ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64" fill="none">
                    <defs>
                      <linearGradient id="micGradient" x1="0" y1="32" x2="64" y2="32" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#43B02A"/>
                        <stop offset="1" stopColor="#86BC25"/>
                      </linearGradient>
                    </defs>
                    <circle cx="32" cy="32" r="32" fill="url(#micGradient)"/>
                    <path d="M31.9999 36.563C30.3421 36.563 28.9464 35.9831 27.8126 34.8233C26.6789 33.6635 26.1121 32.2552 26.1121 30.5985V17.2687C26.1121 15.6716 26.6842 14.3142 27.8286 13.1964C28.9732 12.0787 30.363 11.5198 31.9977 11.5198C33.6328 11.5198 35.0233 12.0787 36.169 13.1964C37.3148 14.3142 37.8877 15.6716 37.8877 17.2687V30.5985C37.8877 32.2552 37.3209 33.6635 36.1871 34.8233C35.0534 35.9831 33.6577 36.563 31.9999 36.563ZM30.1715 52.4798V45.3835C26.3191 44.9523 23.1008 43.3295 20.5166 40.5151C17.9321 37.7008 16.6399 34.3952 16.6399 30.5985H20.2972C20.2972 33.7722 21.4375 36.452 23.7181 38.638C25.9983 40.8236 28.7564 41.9164 31.9922 41.9164C35.2277 41.9164 37.9883 40.8236 40.274 38.638C42.5597 36.452 43.7026 33.7722 43.7026 30.5985H47.3599C47.3599 34.3952 46.0676 37.7008 43.4831 40.5151C40.899 43.3295 37.6807 44.9523 33.8283 45.3835V52.4798H30.1715ZM31.9999 32.9699C32.6461 32.9699 33.1795 32.7394 33.6001 32.2784C34.0206 31.8171 34.2309 31.2571 34.2309 30.5985V17.2687C34.2309 16.6579 34.0172 16.1459 33.5897 15.7327C33.1618 15.3195 32.6318 15.1129 31.9999 15.1129C31.3679 15.1129 30.838 15.3195 30.4101 15.7327C29.9826 16.1459 29.7689 16.6579 29.7689 17.2687V30.5985C29.7689 31.2571 29.9791 31.8171 30.3997 32.2784C30.8203 32.7394 31.3537 32.9699 31.9999 32.9699Z" fill="#1F1F1F"/>
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64" fill="none">
                    <circle cx="32" cy="32" r="32" fill="white"/>
                    <path d="M31.9999 36.563C30.3421 36.563 28.9464 35.9831 27.8126 34.8233C26.6789 33.6635 26.1121 32.2552 26.1121 30.5985V17.2687C26.1121 15.6716 26.6842 14.3142 27.8286 13.1964C28.9732 12.0787 30.363 11.5198 31.9977 11.5198C33.6328 11.5198 35.0233 12.0787 36.169 13.1964C37.3148 14.3142 37.8877 15.6716 37.8877 17.2687V30.5985C37.8877 32.2552 37.3209 33.6635 36.1871 34.8233C35.0534 35.9831 33.6577 36.563 31.9999 36.563ZM30.1715 52.4798V45.3835C26.3191 44.9523 23.1008 43.3295 20.5166 40.5151C17.9321 37.7008 16.6399 34.3952 16.6399 30.5985H20.2972C20.2972 33.7722 21.4375 36.452 23.7181 38.638C25.9983 40.8236 28.7564 41.9164 31.9922 41.9164C35.2277 41.9164 37.9883 40.8236 40.274 38.638C42.5597 36.452 43.7026 33.7722 43.7026 30.5985H47.3599C47.3599 34.3952 46.0676 37.7008 43.4831 40.5151C40.899 43.3295 37.6807 44.9523 33.8283 45.3835V52.4798H30.1715ZM31.9999 32.9699C32.6461 32.9699 33.1795 32.7394 33.6001 32.2784C34.0206 31.8171 34.2309 31.2571 34.2309 30.5985V17.2687C34.2309 16.6579 34.0172 16.1459 33.5897 15.7327C33.1618 15.3195 32.6318 15.1129 31.9999 15.1129C31.3679 15.1129 30.838 15.3195 30.4101 15.7327C29.9826 16.1459 29.7689 16.6579 29.7689 17.2687V30.5985C29.7689 31.2571 29.9791 31.8171 30.3997 32.2784C30.8203 32.7394 31.3537 32.9699 31.9999 32.9699Z" fill="#1F1F1F"/>
                  </svg>
                )}
              </button>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};
